import { useId, useMemo, useState } from 'react';
import { clsx } from 'clsx';

export interface InputFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label?: string;
  onChange?: (key: string, value: string | number) => void;
  id?: string;
  error?: string;
  restrictMinDate?: boolean;
  showClear?: boolean;
}

// Utility to get tomorrow's date
const getTomorrowDate = () => {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split('T')[0];
};

const Input = (props: InputFieldProps) => {
  const {
    label = '',
    className = '',
    onChange,
    id: customId,
    error,
    restrictMinDate = true,
    showClear = true,
    ...inputProps
  } = props;

  const generatedId = useId();
  const id = customId || generatedId;
  const [value, setValue] = useState(inputProps.value ?? '');

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue =
      inputProps.type === 'number' ? e.target.valueAsNumber : e.target.value;
    setValue(e.target.value);
    onChange?.(id, newValue);
  };

  const handleClear = () => {
    setValue('');
    onChange?.(id, inputProps.type === 'number' ? 0 : '');
  };

  const minDateInput = useMemo(() => {
    return inputProps.type === 'date' && restrictMinDate
      ? getTomorrowDate()
      : undefined;
  }, [inputProps.type, restrictMinDate]);

  return (
    <div className={className}>
      {label && (
        <label
          className="text-gray-700 mb-0-5 block text-1-6 md:text-1-2 font-medium"
          htmlFor={id}
        >
          {label} {inputProps.required && '*'}
        </label>
      )}

      <div className="relative">
        <input
          {...inputProps}
          id={id}
          value={value}
          onChange={handleOnChange}
          min={minDateInput}
          className={clsx(
            'mb-0 block w-full appearance-none rounded-8-0 border border-grey-400 p-1-0 pr-3-5 text-body1 leading-1-0 text-grey-800 focus:outline-none read-only:bg-secondary-grey disabled:bg-secondary-grey',
            !!error && 'border-red',
          )}
        />
        {showClear &&
          String(value).length > 0 &&
          !inputProps.readOnly &&
          !inputProps.disabled && (
            <button
              type="button"
              onClick={handleClear}
              aria-label="Clear input"
              className="text-body1 absolute right-4 top-1/2 -translate-y-1/2 text-grey-600 hover:text-grey-800"
            >
              ✕
            </button>
          )}
      </div>

      {error && <p className="text-red-200 text-1-6 mt-1-0">{error}</p>}
    </div>
  );
};

export default Input;
